import { useCallback, useEffect, useRef, useState } from 'react'
import { normalizeAngle, computeRotationFromClient as computeRotationFromClientHelper } from './zoomPanDraggableUtils'

export default function useRotationHandle({
  initialRotation = 0,
  onRotateCommit,
  enableRotationHandle,
  isOverlayActive,
  handleActivate,
  contentRef,
}) {
  const [currentRotation, setCurrentRotation] = useState(() => normalizeAngle(initialRotation))
  const [isRotating, setIsRotating] = useState(false)
  const rotationRef = useRef(normalizeAngle(initialRotation))
  const isRotatingRef = useRef(false)
  const rotationPointerIdRef = useRef(null)
  const rotationMoveHandlerRef = useRef(null)
  const rotationUpHandlerRef = useRef(null)
  const rotationHandleRef = useRef(null)

  // Sync with external rotation prop
  useEffect(() => {
    const next = normalizeAngle(initialRotation)
    if (!isRotatingRef.current) {
      rotationRef.current = next
      setCurrentRotation(next)
    }
  }, [initialRotation])

  const cleanupRotationListeners = useCallback(({ skipStateUpdate = false } = {}) => {
    if (rotationMoveHandlerRef.current) {
      window.removeEventListener('pointermove', rotationMoveHandlerRef.current)
      rotationMoveHandlerRef.current = null
    }
    if (rotationUpHandlerRef.current) {
      window.removeEventListener('pointerup', rotationUpHandlerRef.current)
      window.removeEventListener('pointercancel', rotationUpHandlerRef.current)
      rotationUpHandlerRef.current = null
    }
    rotationPointerIdRef.current = null
    if (!skipStateUpdate && isRotatingRef.current) {
      setIsRotating(false)
    }
    isRotatingRef.current = false
  }, [])

  useEffect(() => {
    return () => {
      cleanupRotationListeners({ skipStateUpdate: true })
    }
  }, [cleanupRotationListeners])

  const handleRotationPointerMove = useCallback(
    (event) => {
      if (!isRotatingRef.current) return
      if (rotationPointerIdRef.current !== null && event.pointerId !== rotationPointerIdRef.current) return

      const next = computeRotationFromClientHelper(contentRef.current, event.clientX, event.clientY)
      if (next === null) return

      rotationRef.current = next
      setCurrentRotation(next)
    },
    [contentRef]
  )

  const finishRotation = useCallback(
    (event) => {
      if (rotationPointerIdRef.current !== null && event.pointerId !== rotationPointerIdRef.current) return

      event?.preventDefault()
      event?.stopPropagation()

      if (!isRotatingRef.current) return

      isRotatingRef.current = false
      setIsRotating(false)
      cleanupRotationListeners()

      if (typeof onRotateCommit === 'function') {
        onRotateCommit(rotationRef.current)
      }
    },
    [cleanupRotationListeners, onRotateCommit]
  )

  const startRotation = useCallback(
    (event) => {
      if (!enableRotationHandle || !isOverlayActive) return

      handleActivate()
      event.preventDefault()
      event.stopPropagation()

      const next = computeRotationFromClientHelper(contentRef.current, event.clientX, event.clientY)
      if (next === null) return

      isRotatingRef.current = true
      setIsRotating(true)
      rotationRef.current = next
      setCurrentRotation(next)

      rotationPointerIdRef.current = event.pointerId ?? null

      if (event.target?.setPointerCapture && event.pointerId !== undefined) {
        try {
          event.target.setPointerCapture(event.pointerId)
        } catch (error) {
          console.error('Pointer capture not supported:', error)
        }
      }

      const moveHandler = (moveEvent) => {
        handleRotationPointerMove(moveEvent)
      }
      const upHandler = (upEvent) => {
        if (rotationHandleRef.current?.releasePointerCapture && upEvent.pointerId !== undefined) {
          try {
            rotationHandleRef.current.releasePointerCapture(upEvent.pointerId)
          } catch (error) {
            console.error('Pointer release not supported:', error)
          }
        }
        finishRotation(upEvent)
      }

      rotationMoveHandlerRef.current = moveHandler
      rotationUpHandlerRef.current = upHandler

      window.addEventListener('pointermove', moveHandler)
      window.addEventListener('pointerup', upHandler)
      window.addEventListener('pointercancel', upHandler)
    },
    [contentRef, enableRotationHandle, finishRotation, handleActivate, handleRotationPointerMove, isOverlayActive]
  )

  return {
    currentRotation,
    isRotating,
    startRotation,
    rotationHandleRef,
    setCurrentRotation,
    setIsRotating,
  }
}
